// NotificationsPage.js
// Shows a feed of notifications for the family

import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  arrayUnion,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import BottomNav from '../components/BottomNav';
import { useAdmin } from '../contexts/AdminContext';

function NotificationsPage({ user, userProfile }) {
  // Admin mode - use viewingFamilyId if set, otherwise user's own familyId
  const { isInAdminView, viewingFamilyId } = useAdmin();
  const effectiveFamilyId = isInAdminView ? viewingFamilyId : userProfile?.familyId;

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Listen for notifications
  useEffect(() => {
    if (!effectiveFamilyId) return;

    const q = query(
      collection(db, 'notifications'),
      where('familyId', '==', effectiveFamilyId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(notifs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [effectiveFamilyId]);

  // Check if notification is read by current user
  const isRead = (notification) => {
    return notification.readBy?.includes(user.uid);
  };

  // Mark a single notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        readBy: arrayUnion(user.uid)
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all unread notifications as read
  const handleMarkAllAsRead = async () => {
    const unreadNotifs = notifications.filter((n) => !isRead(n));
    if (unreadNotifs.length === 0) return;

    try {
      const batch = writeBatch(db);
      unreadNotifs.forEach((notif) => {
        const notifRef = doc(db, 'notifications', notif.id);
        batch.update(notifRef, {
          readBy: arrayUnion(user.uid)
        });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Get notification message based on type
  const getNotificationMessage = (notification) => {
    const { type, actorName, taskTitle } = notification;
    const truncatedTitle = taskTitle?.length > 30
      ? taskTitle.substring(0, 30) + '...'
      : taskTitle;

    switch (type) {
      case 'task_added':
        return `${actorName} added a new task: "${truncatedTitle}"`;
      case 'comment_added':
        return `${actorName} added a comment on "${truncatedTitle}"`;
      case 'task_completed':
        return `${actorName} marked "${truncatedTitle}" as done`;
      default:
        return 'New notification';
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Filter out notifications created by the current user
  const filteredNotifications = notifications.filter(
    (n) => n.actorId !== user.uid
  );

  // Separate into unread and read
  const unreadNotifications = filteredNotifications.filter((n) => !isRead(n));
  const readNotifications = filteredNotifications.filter((n) => isRead(n));

  // Combine with unread first
  const sortedNotifications = [...unreadNotifications, ...readNotifications];

  if (loading) {
    return (
      <div className="notifications-page">
        <div className="notifications-header">
          <h1>Notifications</h1>
        </div>
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
        <BottomNav user={user} userProfile={userProfile} />
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <h1>Notifications</h1>
        {unreadNotifications.length > 0 && (
          <button
            className="mark-all-read-button"
            onClick={handleMarkAllAsRead}
          >
            Mark all as read
          </button>
        )}
      </div>

      {sortedNotifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔔</div>
          <p>No notifications yet</p>
          <p style={{ fontSize: '14px' }}>
            You'll see updates here when family members add tasks or comments.
          </p>
        </div>
      ) : (
        <div className="notifications-list">
          {sortedNotifications.map((notification) => {
            const read = isRead(notification);
            return (
              <div
                key={notification.id}
                className={`notification-item ${read ? 'read' : 'unread'}`}
              >
                <div className="notification-content">
                  <p className="notification-message">
                    {getNotificationMessage(notification)}
                  </p>
                  <span className="notification-time">
                    {formatTimestamp(notification.createdAt)}
                  </span>
                </div>
                {!read && (
                  <button
                    className="mark-read-button"
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    Mark read
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <BottomNav />
    </div>
  );
}

export default NotificationsPage;
